import { useEffect, useState } from 'react';
import API from '@/lib/api';
import MailPreviewModal from '@/components/MailPreviewModal';

export default function VersturenModal({ doc, klant, onClose, onSent }) {
  const [opties, setOpties] = useState(null);
  const [historiek, setHistoriek] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    let ok = true;
    Promise.all([
      API.get(`/documenten/${doc.id}/verzend-opties`),
      API.get(`/documenten/${doc.id}/historiek`),
    ]).then(([o, h]) => {
      if (!ok) return;
      setOpties(o.data);
      setHistoriek(h.data || []);
    }).catch(() => alert('Kan verzend-opties niet laden'));
    return () => { ok = false; };
  }, [doc.id]);

  const verstuurPeppol = async () => {
    setBusy(true);
    try {
      const r = await API.post(`/documenten/${doc.id}/verzend-peppol`);
      alert(`Factuur verzonden via Peppol → ${r.data.participant}`);
      onSent?.();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Peppol verzenden mislukt');
      setBusy(false);
    }
  };

  // E-mail: open eerst preview-popup, daarna verzendt de preview-modal
  const openEmailPreview = () => {
    if (!klant?.email) { alert('Klant heeft geen e-mailadres'); return; }
    setShowPreview(true);
  };

  const peppolOk = opties?.peppol?.enabled;
  const emailOk = opties?.email?.enabled;
  const kNaam = klant?.bedrijf || `${klant?.voorn || ''} ${klant?.achtern || ''}`.trim() || '-';

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => !busy && onClose()} data-testid="versturen-modal">
      <div className="bg-white border border-gray-200 shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-[#0A0A0A]">Factuur versturen</h3>
            <p className="text-xs text-gray-500 mt-0.5">{doc.id} — {kNaam}</p>
          </div>
          <button className="text-gray-400 hover:text-gray-700 text-xl leading-none" onClick={() => !busy && onClose()} data-testid="versturen-close">×</button>
        </div>

        {!opties ? (
          <div className="p-8 text-center text-sm text-gray-400">Laden…</div>
        ) : (
          <div className="p-5 space-y-4">
            {/* Peppol optie */}
            <div className={`border p-4 ${peppolOk ? 'border-[#059669]/40 bg-green-50/40' : 'border-gray-200 bg-gray-50/40'}`} data-testid="versturen-peppol-card">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#059669]">Peppol</span>
                    <span className={`text-[9px] font-bold px-1.5 py-0.5 ${peppolOk ? 'bg-[#059669] text-white' : 'bg-gray-300 text-gray-600'}`}>
                      {peppolOk ? 'AANBEVOLEN' : 'NIET BESCHIKBAAR'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-600 leading-snug">
                    {peppolOk
                      ? <>Directe levering via het officiële Peppol-netwerk naar <strong className="font-mono">{opties.peppol.display}</strong></>
                      : <>{opties.peppol.reason}. Schakel de Peppol-vlag in bij de klant indien aangesloten.</>}
                  </div>
                </div>
                <button
                  className="btn btn-sm inline-flex items-center gap-1.5 shrink-0"
                  style={{ background: peppolOk ? '#059669' : '#D1D5DB', color: peppolOk ? '#fff' : '#6B7280', cursor: peppolOk && !busy ? 'pointer' : 'not-allowed' }}
                  disabled={!peppolOk || busy}
                  onClick={verstuurPeppol}
                  data-testid="versturen-peppol-btn"
                >
                  Verzenden via Peppol
                </button>
              </div>
            </div>

            {/* E-mail optie */}
            <div className={`border p-4 ${emailOk ? 'border-[#002FA7]/40 bg-[#F0F4FF]/40' : 'border-gray-200 bg-gray-50/40'}`} data-testid="versturen-email-card">
              <div className="flex items-start justify-between">
                <div className="flex-1 pr-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#002FA7]">E-mail</span>
                    {!peppolOk && emailOk && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-[#002FA7] text-white">AANBEVOLEN</span>}
                    {!emailOk && <span className="text-[9px] font-bold px-1.5 py-0.5 bg-gray-300 text-gray-600">GEEN ADRES</span>}
                  </div>
                  <div className="text-xs text-gray-600 leading-snug">
                    {emailOk
                      ? <>PDF verzenden naar <strong>{opties.email.to}</strong></>
                      : <>{opties.email.reason}. Voeg eerst een e-mailadres toe bij de klant.</>}
                  </div>
                </div>
                <button
                  className="btn btn-sm inline-flex items-center gap-1.5 shrink-0"
                  style={{ background: emailOk ? '#002FA7' : '#D1D5DB', color: emailOk ? '#fff' : '#6B7280', cursor: emailOk && !busy ? 'pointer' : 'not-allowed' }}
                  disabled={!emailOk || busy}
                  onClick={openEmailPreview}
                  data-testid="versturen-email-btn"
                >
                  Voorbeeld & Verzenden
                </button>
              </div>
            </div>

            {/* Historiek */}
            <div>
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Verzendhistoriek</div>
              {historiek.length === 0 ? (
                <div className="text-xs text-gray-400 italic py-2">Nog geen verzendingen geregistreerd.</div>
              ) : (
                <div className="border border-gray-100 divide-y divide-gray-100" data-testid="versturen-historiek">
                  {historiek.map((h, i) => (
                    <div key={i} className="flex items-center gap-3 px-3 py-2 text-xs">
                      <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 ${h.type === 'peppol' ? 'bg-[#059669] text-white' : 'bg-[#002FA7] text-white'}`}>{h.type}</span>
                      <span className="text-gray-700 flex-1 truncate">{h.correspondent || h.omschrijving || '-'}</span>
                      <span className="text-gray-400 shrink-0">{h.verzonden_at ? new Date(h.verzonden_at).toLocaleString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showPreview && (
        <MailPreviewModal
          docId={doc.id}
          lang="auto"
          onClose={() => setShowPreview(false)}
          onSent={() => { setShowPreview(false); onSent?.(); onClose(); }}
        />
      )}
    </div>
  );
}
